import { useState } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Textarea,
  Text,
  Box,
  Badge,
  Radio,
  RadioGroup,
  Stack,
  Input,
  Grid,
  IconButton,
} from '@chakra-ui/react';
import ModalHeaderComponent from '../../../../components/ModalHeader';
import { requestServerApi, serverApi } from '../../../../api/api';
import { getBadgePropsByStatus } from '../../../../utils/badgeUtils';
import { getDateAndTimeInterval } from '../../../../components/Format';
import { FaMinus } from 'react-icons/fa';

// Type for each file entry
type FileEntry = {
  id: number;
  shareType: 'file' | 'url';
  file: File | null;
  url: string;
};

type Props = {
  onClose: () => void;
  selectedItem: any;
  onUpdateSuccess: (msg: string) => void;
  learnerIdArray: string[];
};

export default function BulkFeedbackUpdateModal({
  onClose,
  selectedItem,
  onUpdateSuccess,
  learnerIdArray,
}: Props) {
  const [feedback, setFeedback] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [confirming, setConfirming] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([
    { id: Date.now(), shareType: 'file', file: null, url: '' },
  ]);

  const addFileEntry = () => {
    setFileEntries((prev) => [
      ...prev,
      { id: Date.now(), shareType: 'file', file: null, url: '' },
    ]);
  };

  const removeFileEntry = (id: number) => {
    setFileEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateFileEntry = (id: number, changes: Partial<FileEntry>) => {
    setFileEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry))
    );
  };

  const closeModal = () => {
    onClose();
  };

  const updateBulkFeedback = () => {
    setIsUpdating(true);
    // Filter out empty entries
    const validFileEntries = fileEntries.filter((entry) =>
      (entry.shareType === 'file' && entry.file !== null) ||
      (entry.shareType === 'url' && entry.url.trim() !== '')
    );

    const filesMetadata = validFileEntries.map((entry) => ({
      is_internal_file: entry.shareType === 'file',
      file_url: entry.shareType === 'url' ? entry.url : '',
    }));

    const internalFiles = validFileEntries
      .filter((entry) => entry.shareType === 'file' && entry.file)
      .map((entry) => entry.file as File);

    // Build a FormData with actual files to merge into the generated one
    const extraFormData = new FormData();
    internalFiles.forEach((file) => {
      extraFormData.append('files', file); // "files" must match FilesInterceptor("files") in controller
    });

    const apiResponse = requestServerApi<any>(() =>
      serverApi.organizationsControllerUpdateLessonFeedback(
        selectedItem.company_id,
        selectedItem.lesson_id,
        feedback,
        learnerIdArray.join(","), // Convert array to CSV string
        adminComment,
        JSON.stringify(filesMetadata),
        {
          transformRequest: [
            (data: FormData) => {
              // append files into it before sending
              internalFiles.forEach((file) => {
                data.append('files', file);
              });
              return data;
            },
          ],
        }
      )
    );

    apiResponse.then((result) => {
      onUpdateSuccess(result.data.message);
      onClose();
      setIsUpdating(false);
    });
  };
  return (
    <Modal isOpen={true} onClose={onClose} closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent minW="40rem">
        <ModalHeaderComponent
          title={confirming === false ? 'フィードバック一括編集' : 'フィードバック一括編集　確認'}
          disableClose={isUpdating}
        />

        <ModalBody>
          {/* 実施日時 + 回数（ダミー） */}
          <Grid templateColumns="auto auto" columnGap="40px">
            <Flex direction="column">
              <Text fontWeight="bold">実施日時</Text>
              <Text>{getDateAndTimeInterval(selectedItem?.start_datetime, selectedItem?.end_datetime)}</Text>
            </Flex>
          </Grid>

          {/* 受講者 */}
          <Flex direction="column" mt={5}>
            <Text fontWeight="bold">受講者</Text>

            {Array.isArray(selectedItem?.t_lesson_learner) && selectedItem.t_lesson_learner.length > 0 &&
              selectedItem.t_lesson_learner.map((learner: any, index: number) => {
                const badge = getBadgePropsByStatus(learner?.participation_status);
                return (
                  <Flex
                    key={index}
                    mt={2}
                    align="center"
                    justify="space-between"
                    maxW="300px"
                  >
                    <Text>{learner?.m_learner?.full_name}</Text>

                    <Badge colorScheme={badge.colorScheme} fontSize="md" px={2} py={1}>
                      {badge.label}
                    </Badge>
                  </Flex>
                );
              })}
          </Flex>
          {/* 内容 */}
          <Box mt={5}>
            <Text fontWeight="bold">内容</Text>

            {confirming ? (
              <Text mt={2} color="black" whiteSpace="pre-wrap">
                {feedback}
              </Text>
            ) : (
              <Textarea
                mt={2}
                rows={10}
                placeholder="レッスンの内容や学習者についてのフィードバックを記入してください。"
                onChange={(e) => setFeedback(e.target.value)}
                value={feedback}
              />
            )}
          </Box>
          {/* 学習者の管理者へのコメント */}
          <Box mt={5}>
            <Text fontWeight="bold">
              学習者の管理者へのコメント（学習者には見えません）
            </Text>

            {confirming ? (
              <Text mt={2} color="black" whiteSpace="pre-wrap">
                {adminComment || 'コメントが入力されていません'}
              </Text>
            ) : (
              <Textarea
                mt={2}
                rows={5}
                placeholder="学習者の管理者へのコメントを記入してください。"
                onChange={(e) => setAdminComment(e.target.value)}
                value={adminComment}
              />
            )}
          </Box>

          {/* 共有資料 */}
          {!confirming && (
            <Box mt={5}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold">共有資料</Text>
                {/* Add new file entry button — hidden in confirming mode */}
                {!confirming && (
                  <Button size="sm" colorScheme="teal" variant="outline" onClick={addFileEntry}>
                    ＋ 追加
                  </Button>
                )}
              </Flex>

              {fileEntries.map((entry, index) => (
                <Box key={entry.id} mt={4} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm" color="gray.500">資料 {index + 1}</Text>

                    {/* Remove button — hidden in confirming mode */}
                    {!confirming && fileEntries.length > 1 && (
                      <IconButton
                        aria-label="削除"
                        icon={<FaMinus />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removeFileEntry(entry.id)}
                      />
                    )}
                  </Flex>

                  {confirming ? (
                    // Confirm view — show summary of each entry
                    <Text mt={2} fontSize="sm" color="black">
                      {entry.shareType === 'file'
                        ? entry.file?.name || 'ファイル未選択'
                        : entry.url || 'URLが入力されていません'}
                    </Text>
                  ) : (
                    // Edit view
                    <>
                      <RadioGroup
                        mt={2}
                        value={entry.shareType}
                        onChange={(value: 'file' | 'url') =>
                          updateFileEntry(entry.id, { shareType: value, file: null, url: '' })
                        }
                      >
                        <Stack direction="row">
                          <Radio value="file">ファイル</Radio>
                          <Radio value="url">URL</Radio>
                        </Stack>
                      </RadioGroup>

                      {entry.shareType === 'file' ? (
                        <Input
                          mt={2}
                          type="file"
                          onChange={(e) =>
                            updateFileEntry(entry.id, { file: e.target.files?.[0] || null, url: '' })
                          }
                        />
                      ) : (
                        <Input
                          mt={2}
                          type="text"
                          placeholder="https://example.com"
                          value={entry.url}
                          onChange={(e) =>
                            updateFileEntry(entry.id, { url: e.target.value, file: null })
                          }
                        />
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </ModalBody>

        {confirming === false ? (
          <ModalFooter>
            <Button
              colorScheme="teal"
              mr={3}
              onClick={() => setConfirming(true)}
              disabled={
                isUpdating ||
                ((feedback.trim() === '' && adminComment.trim() === '') &&
                  // check if first element of fileEntries is empty (default state) to prevent confirming without any input, or if all entries are empty
                  ((fileEntries.length === 1 &&
                    fileEntries[0].shareType === 'file' &&
                    !fileEntries[0].file) ||
                    (fileEntries.length === 1 &&
                      fileEntries[0].shareType === 'url' &&
                      fileEntries[0].url.trim() === ''
                    )))
              }
            >
              確認
            </Button>
            <Button colorScheme="gray" mr={3} onClick={closeModal}>
              キャンセル
            </Button>
          </ModalFooter>
        ) : (
          <ModalFooter>
            <Flex justifyContent="right">
              <Button
                colorScheme="teal"
                mr={3}
                onClick={updateBulkFeedback}
                disabled={isUpdating}
              >
                編集
              </Button>
              <Button
                colorScheme="gray"
                mr={3}
                onClick={() => setConfirming(false)}
                disabled={isUpdating}
              >
                修正
              </Button>
            </Flex>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}